import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Server, Trash2, Lock, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PrivacyPage = () => {
  const { language } = useLanguage();

  const sections = language === 'nl' ? [
    {
      icon: Eye,
      title: 'Welke gegevens verzamelen wij?',
      content: [
        'Account gegevens: Bij registratie vragen wij om uw e-mailadres, naam en optioneel bedrijfsnaam. Dit is nodig om uw account aan te maken en u toegang te geven tot onze diensten.',
        'Betalingskenmerken: De betalingskenmerken die u invoert worden NIET opgeslagen op onze servers. Ze worden alleen verwerkt om u direct het resultaat te tonen.',
        'Gebruiksstatistieken: Wij houden bij hoeveel decoderingen u heeft uitgevoerd voor uw accountoverzicht.',
        'Technische gegevens: IP-adres en browserinformatie voor beveiligingsdoeleinden en het verbeteren van onze dienst.'
      ]
    },
    {
      icon: Server,
      title: 'Hoe gebruiken wij uw gegevens?',
      content: [
        'Om u toegang te geven tot onze decodeer-dienst',
        'Om uw account te beheren en u te informeren over wijzigingen',
        'Om onze dienstverlening te verbeteren',
        'Om fraude en misbruik te voorkomen',
        'Om te voldoen aan wettelijke verplichtingen'
      ]
    },
    {
      icon: Shield,
      title: 'Privacy by Design',
      content: [
        'Betalingskenmerken worden NOOIT opgeslagen in onze database',
        'Alle communicatie verloopt via beveiligde HTTPS-verbindingen',
        'Wachtwoorden worden versleuteld opgeslagen met moderne encryptie',
        'Wij verkopen of delen uw gegevens NIET met derden',
        'Alleen essentiële gegevens worden bewaard'
      ]
    },
    {
      icon: Lock,
      title: 'Beveiliging',
      content: [
        'Wij gebruiken industriestandaard beveiligingsmaatregelen om uw gegevens te beschermen',
        'Regelmatige beveiligingsaudits en updates',
        'Beperkte toegang tot persoonlijke gegevens binnen ons team',
        'Versleutelde opslag van gevoelige informatie'
      ]
    },
    {
      icon: Trash2,
      title: 'Uw rechten',
      content: [
        'Recht op inzage: U kunt opvragen welke gegevens wij van u bewaren',
        'Recht op correctie: U kunt onjuiste gegevens laten aanpassen',
        'Recht op verwijdering: U kunt verzoeken om verwijdering van uw account en gegevens',
        'Recht op bezwaar: U kunt bezwaar maken tegen bepaalde verwerkingen',
        'Neem contact met ons op via privacy@kenmerkcheck.nl voor het uitoefenen van uw rechten'
      ]
    },
    {
      icon: Mail,
      title: 'Contact',
      content: [
        'Voor vragen over ons privacybeleid kunt u contact opnemen via:',
        'E-mail: privacy@kenmerkcheck.nl',
        'Wij streven ernaar binnen 30 dagen te reageren op uw verzoeken.'
      ]
    }
  ] : [
    {
      icon: Eye,
      title: 'What data do we collect?',
      content: [
        'Account data: Upon registration, we ask for your email address, name, and optionally company name. This is necessary to create your account and provide access to our services.',
        'Payment references: The payment references you enter are NOT stored on our servers. They are only processed to show you the result immediately.',
        'Usage statistics: We track how many decodings you have performed for your account overview.',
        'Technical data: IP address and browser information for security purposes and improving our service.'
      ]
    },
    {
      icon: Server,
      title: 'How do we use your data?',
      content: [
        'To provide you access to our decoding service',
        'To manage your account and inform you about changes',
        'To improve our service delivery',
        'To prevent fraud and abuse',
        'To comply with legal obligations'
      ]
    },
    {
      icon: Shield,
      title: 'Privacy by Design',
      content: [
        'Payment references are NEVER stored in our database',
        'All communication is via secure HTTPS connections',
        'Passwords are stored encrypted with modern encryption',
        'We do NOT sell or share your data with third parties',
        'Only essential data is retained'
      ]
    },
    {
      icon: Lock,
      title: 'Security',
      content: [
        'We use industry-standard security measures to protect your data',
        'Regular security audits and updates',
        'Limited access to personal data within our team',
        'Encrypted storage of sensitive information'
      ]
    },
    {
      icon: Trash2,
      title: 'Your rights',
      content: [
        'Right to access: You can request what data we hold about you',
        'Right to correction: You can have incorrect data corrected',
        'Right to deletion: You can request deletion of your account and data',
        'Right to object: You can object to certain processing',
        'Contact us at privacy@kenmerkcheck.nl to exercise your rights'
      ]
    },
    {
      icon: Mail,
      title: 'Contact',
      content: [
        'For questions about our privacy policy, you can contact us via:',
        'Email: privacy@kenmerkcheck.nl',
        'We aim to respond to your requests within 30 days.'
      ]
    }
  ];

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {language === 'nl' ? 'Privacybeleid' : 'Privacy Policy'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {language === 'nl' 
              ? 'Laatst bijgewerkt: december 2025'
              : 'Last updated: December 2025'}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 p-6 bg-accent/5 border border-accent/20 rounded-xl"
        >
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            {language === 'nl' 
              ? 'KenmerkCheck hecht veel waarde aan uw privacy. In dit privacybeleid leggen wij uit welke persoonsgegevens wij verzamelen, waarom wij dit doen en hoe wij hiermee omgaan. Wij verwerken uw gegevens in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG).'
              : 'KenmerkCheck values your privacy. In this privacy policy, we explain what personal data we collect, why we do so, and how we handle it. We process your data in accordance with the General Data Protection Regulation (GDPR).'}
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-accent" />
                  </div>
                  <h2 className="font-heading font-bold text-xl text-primary dark:text-white">
                    {section.title}
                  </h2>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                      <span className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          ))}
        </div>

        {/* Cookies notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
        >
          <h3 className="font-heading font-bold text-lg text-primary dark:text-white mb-3">
            {language === 'nl' ? 'Cookies' : 'Cookies'}
          </h3>
          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
            {language === 'nl' 
              ? 'Wij gebruiken alleen functionele cookies die noodzakelijk zijn voor het functioneren van de website (zoals het onthouden van uw taalvoorkeur en thema). Wij gebruiken geen tracking cookies of analytics van derden.'
              : 'We only use functional cookies necessary for the website to function (such as remembering your language preference and theme). We do not use tracking cookies or third-party analytics.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
